import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Shield, Star, Clock } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();
  const [trialConfig, setTrialConfig] = useState({ trialDurationDays: 14, maxUsers: 5, features: [] });
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    
    // Fetch trial config
    fetch(`${apiUrl}/subscriptions/config`)
      .then(r => r.json())
      .then(d => { 
        if (d) setTrialConfig({
          trialDurationDays: d.trialDurationDays || 14,
          maxUsers: d.maxUsers || 5,
          features: d.features || []
        }); 
      })
      .catch(() => {});

    // Fetch active plans
    fetch(`${apiUrl}/subscription-plans/active`)
      .then(r => r.json())
      .then(d => { 
        if (Array.isArray(d)) setPlans(d);
        setLoadingPlans(false);
      })
      .catch(() => setLoadingPlans(false));
  }, []);

  const defaultTrialFeatures = [
    'Unlimited Lead Management',
    'Multi-role Team Access',
    'Site Visit Scheduling',
    'Advanced Lead Filtering',
    'Real-time Dashboard',
    'Notification Center',
  ];

  const trialFeatures = trialConfig.features && trialConfig.features.length > 0 
    ? trialConfig.features 
    : defaultTrialFeatures;

  const allDisplayPlans = [
    {
      _id: 'trial',
      name: 'Free Trial',
      price: 0,
      durationDays: trialConfig.trialDurationDays,
      maxUsers: trialConfig.maxUsers,
      features: trialFeatures,
      isTrial: true
    },
    ...plans
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      fontFamily: "'Inter', sans-serif",
      color: '#1e293b',
      paddingBottom: '100px'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(15px);} to { opacity:1; transform:translateY(0);} }
        .pricing-card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04) !important; }
        .plan-cta:hover { background: #2563eb !important; }
      `}</style>

      <nav className="pricing-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 48px', borderBottom: '1px solid #f1f5f9', background: '#ffffff', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="white" />
          </div>
          <span style={{ fontWeight: '800', fontSize: '18px', color: '#1e293b' }}>{appName}</span>
        </div>
        <button onClick={() => navigate('/login')} style={{ background: 'transparent', border: '1px solid #e2e8f0', color: '#475569', padding: '9px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Sign In</button>
      </nav>

      <div className="pricing-header" style={{ textAlign: 'center', padding: '80px 24px 60px', animation: 'fadeUp 0.7s ease' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#eff6ff', border: '1px solid #dbeafe', padding: '7px 18px', borderRadius: '100px', marginBottom: '24px', fontSize: '13px', fontWeight: '600', color: '#2563eb' }}>
          <Zap size={14} fill="#2563eb" /> Transparent pricing for teams of all sizes
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '800', letterSpacing: '-0.03em', color: '#1e293b', marginBottom: '20px' }}>
          Choose your perfect plan
        </h1>
        <p style={{ fontSize: '18px', color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
          Start with our risk-free {trialConfig.trialDurationDays}-day trial. No credit card required.
        </p>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        flexWrap: 'wrap', 
        gap: '24px', 
        justifyContent: 'center', 
        padding: '0 24px',
        animation: 'fadeUp 0.9s ease'
      }}>
        {allDisplayPlans.map((plan, i) => (
          <div key={i} className="pricing-card" style={{ 
            width: '320px', 
            background: plan.isTrial ? '#f8fafc' : '#ffffff', 
            border: plan.isTrial ? '1px solid #3b82f6' : '1px solid #e2e8f0', 
            borderRadius: '24px', 
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            position: 'relative',
            minHeight: '540px'
          }}>
            {plan.isTrial && (
              <div style={{ position: 'absolute', top: '24px', right: '24px', background: '#3b82f6', color: 'white', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '100px', textTransform: 'uppercase' }}>Current</div>
            )}
            
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>{plan.name}</h3>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '44px', fontWeight: '800', color: '#1e293b' }}>₹{plan.price}</span>
                <span style={{ fontSize: '15px', color: '#64748b' }}>/{plan.durationDays} days</span>
              </div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px 18px', marginBottom: '32px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>User Limit</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Up to {plan.maxUsers} active users</div>
            </div>

            <div style={{ flex: 1, marginBottom: '32px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '20px' }}>What's included:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                 {plan.features.slice(0, 7).map((f, idx) => (
                   <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#475569' }}>
                     <Check size={17} color="#10b981" strokeWidth={2.5} /> {f}
                   </div>
                 ))}
              </div>
            </div>

            <button 
              onClick={() => navigate(plan.isTrial ? '/register' : `/register?planId=${plan._id}`)} 
              style={{ 
                width: '100%', 
                padding: '16px', 
                background: plan.isTrial ? '#3b82f6' : '#f1f5f9', 
                border: 'none', 
                borderRadius: '14px', 
                color: plan.isTrial ? 'white' : '#1e293b', 
                fontSize: '16px', 
                fontWeight: '700', 
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              className="plan-cta"
            >
              {plan.isTrial ? 'Get Started' : 'Choose Plan'}
            </button>
          </div>
        ))}
      </div>

      {/* ── Legal Footer ── */}
      <div style={{
        marginTop: '100px', padding: '48px 24px',
        borderTop: '1px solid #f1f5f9',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
      }}>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>
          © {new Date().getFullYear()} {appName}. Built for high-performance teams.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px' }}>
          {[
            { label: 'Privacy Policy', path: '/privacy-policy' },
            { label: 'Terms & Conditions', path: '/terms' },
            { label: 'Refund Policy', path: '/payment-policy' },
            { label: 'Cookie Policy', path: '/cookie-policy' },
          ].map(lnk => (
            <a key={lnk.path} href={lnk.path} style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500' }}>{lnk.label}</a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;

